import { Eye, File } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../../../context/AuthContext";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import { DataTable } from "../../../../DataTable/DataTable";
import { OfflineOrderDetails } from "../OfflineOrder/OfflineOrderDetails";
import CreateOrder from "./CreateOrder";
import Bill from "../../../../../shared/Bill";

export interface OfllineOrderData {
  orderId: string;
  invoiceNumber: string;
  patientName: string;
  patientMobileNumber: string;
  doctorName: string;
  paymentAmount: number;
  paymentType: string;
  cashPaid: number;
  coinsUsed: number;
  onlinePaid: number;
  orderDate: string;
  itemsCount: number;
}

const OfflineOrder = () => {
  const [offlineOrders, setOfflineOrders] = useState<OfllineOrderData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<any>(null);
  const [showOfflineOrderDetails, setShowOfflineOrderDetails] =
    useState<any>(null);
  const [showOfflineOrder, setShowOfflineOrder] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const { user } = useAuth();
  const [searchBy, setSearchBy] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [showBillPreview, setShowBillPreview] = useState(false);
  const clinicId = user?.role === "clinic" ? user.id : "";
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
    nextPage: null as number | null,
    previousPage: null as number | null,
  });

  const fetchOfflineOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await OfflineItemService.getOfflineOrderList({
        page: Number(pagination.currentPage),
        pageSize: Number(pagination.itemsPerPage),
        clinicId: clinicId,
        searchBy: searchBy,
        searchValue: searchValue,
        date: selectedDate,
      });

      if (response && response.success) {
        setOfflineOrders(response.data.data);
        setPagination((prev) => ({
          ...prev,
          totalItemsCount: response.data.pagination.totalCount,
          totalPages: response.data.pagination.totalPages,
          nextPage: response.data.pagination.hasNextPage
            ? prev.currentPage + 1
            : null,
          previousPage: response.data.pagination.hasPrevPage
            ? prev.currentPage - 1
            : null,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    pagination.currentPage,
    pagination.itemsPerPage,
    searchBy,
    searchValue,
    selectedDate,
    clinicId,
  ]);

  useEffect(() => {
    fetchOfflineOrders();
  }, [fetchOfflineOrders]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination((prev) => ({ ...prev, itemsPerPage, currentPage: 1 }));
  };

  const fetchOrderDetails = async (orderId: string) => {
    try {
      setIsLoading(true);
      const response =
        await OfflineItemService.getOfflineOrderDetailsByOrderId(orderId);
      if (response && response.success) {
        setSelectedOrderDetails(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch order details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (orderId: string) => {
    setSelectedOrderId(orderId);
    await fetchOrderDetails(orderId);
    setShowOfflineOrderDetails(true);
  };
  if (showOfflineOrder) {
    return (
      <div className="w-full h-[600px]">
        <CreateOrder
          handleBack={() => {
            setShowOfflineOrder(false);
            fetchOfflineOrders();
          }}
          onOrderSuccess={() => {
            setShowOfflineOrder(false);
            fetchOfflineOrders();
          }}
        />
      </div>
    );
  }

  const handleViewBill = (orderId: string) => {
    setShowBillPreview(true);
    setSelectedOrderId(orderId);
  };

  const handleCloseBillPreview = () => {
    setShowBillPreview(false);
    setSelectedOrderId("");
  };
  const columns = [
    {
      key: "orderId",
      title: "Order ID",
      sortable: true,
      render: (offline: OfllineOrderData) => <div>{offline.invoiceNumber || "N/A"}</div>,
    },
    {
      key: "patientName",
      title: "Patient Name",
      sortable: true,
      render: (offline: OfllineOrderData) => <div>{offline.patientName || "N/A"}</div>,
    },
    {
      key: "patientMobileNumber",
      title: "Mobile Number",
      sortable: true,
      render: (offline: OfllineOrderData) => (
        <div>{offline.patientMobileNumber || "N/A"}</div>
      ),
    },
    {
      key: "doctorName",
      title: "Doctor Name",
      sortable: true,
      render: (offline: OfllineOrderData) => <div>{offline.doctorName || "N/A"}</div>,
    },
    {
      key: "totalItems",
      title: "Items",
      sortable: true,
      render: (offline: OfllineOrderData) => <div>{offline.itemsCount || 0}</div>,
    },
    {
      key: "totalAmount",
      title: "Amount",
      sortable: true,
      render: (offline: OfllineOrderData) => <div>₹{offline.paymentAmount || "0.00"}</div>,
    },
    {
      key: "cashPaid",
      title: "Cash Paid",
      sortable: true,
      render: (offline: OfllineOrderData) => <div>₹{offline.cashPaid || "0.00"}</div>,
    },
    {
      key: "onlinePaid",
      title: "Online Paid",
      sortable: true,
      render: (offline: OfllineOrderData) => <div>₹{offline.onlinePaid || "0.00"}</div>,
    },
    {
      key: "orderDate",
      title: "Order Date",
      sortable: true,
      render: (offline: OfllineOrderData) => {
        const date = offline?.orderDate ? new Date(offline.orderDate) : null;
        return <div>{date ? date.toLocaleDateString() : "N/A"}</div>;
      },
    },
    {
      key: "bill",
      title: "Bill",
      render: (offline: OfllineOrderData) => (
        <button
          onClick={() => handleViewBill(String(offline.orderId))}
          className="text-blue-500 hover:text-blue-700"
        >
          <File className="w-5 h-5" />
        </button>
      ),
    },
    {
      key: "action",
      title: "Action",
      render: (offline: any) => (
        <button
          onClick={() => handleViewDetails(offline.orderId)}
          className="text-blue-500 hover:text-blue-700"
        >
          <Eye className="w-5 h-5" />
        </button>
      ),
    },
  ];

  if (showOfflineOrderDetails && selectedOrderDetails) {
    return (
      <div className="p-4">
        <OfflineOrderDetails
          data={selectedOrderDetails}
          setShowOfflineOrderDetails={setShowOfflineOrderDetails}
        />
      </div>
    );
  }
  return (
    <div className="p-4">
      {showBillPreview && selectedOrderId ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto relative"
            style={{
              scrollbarWidth: "thin",
            }}
          >
            <Bill
              orderId={Number(selectedOrderId)}
              orderType="OFFLINE"
              onClose={handleCloseBillPreview}
            />
          </div>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3 mb-4">
        <button
          onClick={() => setShowOfflineOrder(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
        >
          + Add New Order
        </button>

        {/* Select */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border px-3 py-2 rounded"
          />

          {/* Search Input */}
          <input
            type="text"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="border px-3 py-2 rounded w-48"
          />

          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            className="border px-3 py-2 rounded w-48"
          >
            <option value="">Search By</option>
            <option value="patient_name">Patient Name</option>
            <option value="patient_mobile_number">Mobile Number</option>
            <option value="invoice_number">Invoice Number</option>
          </select>
        </div>
      </div>

      <DataTable
        data={offlineOrders}
        columns={columns}
        itemsPerPage={pagination.itemsPerPage}
        isLoading={isLoading}
        pagination={{
          currentPage: pagination.currentPage,
          setCurrentPage: handlePageChange,
          setPageSize: handleItemsPerPageChange,
          totalPages: pagination.totalPages,
          pageSize: pagination.itemsPerPage,
        }}
      />
    </div>
  );
};

export default OfflineOrder;
